"use client";

import { useQuery } from "@apollo/client";
import KPIOrgAppreciations from "components/ui/v2/home/bundle/Dashboard/Charts/KPI/KPIOrgAppreciations";
import KPIOrgCareers from "components/ui/v2/home/bundle/Dashboard/Charts/KPI/KPIOrgCareers";
import KPIPeopleAndTeams from "components/ui/v2/home/bundle/Dashboard/Charts/KPI/KPIOrgPeopleAndTeams";
import KPIOrgTaskAndGoals from "components/ui/v2/home/bundle/Dashboard/Charts/KPI/KPIOrgTaskAndGoals";
import { gqlDashboard } from "gql";
import { IOrganizationalDashboardData } from "gql/Dashboard/queries";
import { Goal, Route, Star, Users } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import type React from "react";

function SectionTitle({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
        {title}
      </h2>
    </div>
  );
}

export function DashboardOrganizationAllPanelV2() {
  const { t } = useTranslation("common");
  const { data: organizationalDashboardData, loading: loadingDashboard } =
    useQuery<{
      organizationalDashboardData: IOrganizationalDashboardData;
    }>(gqlDashboard.queries.GET_ORGANIZATIONA_DASHBOARD_DATA);

  const dashboard = organizationalDashboardData?.organizationalDashboardData;

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <SectionTitle
          icon={<Users className="h-4 w-4 text-indigo-500" />}
          title={t("Personas y equipos")}
        />
        <KPIPeopleAndTeams
          activePeople={dashboard?.activePeople ?? 0}
          teams={dashboard?.teams ?? 0}
          roles={dashboard?.roles ?? 0}
          groowcityAdherence={dashboard?.groowcityAdherence ?? 0}
          loading={loadingDashboard}
        />
      </section>

      <section className="space-y-3">
        <SectionTitle
          icon={<Goal className="h-4 w-4 text-emerald-500" />}
          title={t("Task and goals")}
        />
        <KPIOrgTaskAndGoals
          achievedInTime={[
            dashboard?.goalAchievedOnTime || 0,
            dashboard?.totalGoals ?? 0,
          ]}
          accomplishedOutOfTime={[
            dashboard?.goalAchievedOutOfTime || 0,
            dashboard?.totalGoals ?? 0,
          ]}
          notAchieved={[
            dashboard?.goalNotAchieved || 0,
            dashboard?.totalGoals ?? 0,
          ]}
          performance={dashboard?.goalPerformance || 0}
          loading={loadingDashboard}
        />
      </section>

      <section className="space-y-3">
        <SectionTitle
          icon={<Route className="h-4 w-4 text-purple-500" />}
          title={t("Planes")}
        />
        <KPIOrgCareers
          careersCompleted={[
            dashboard?.careersCompleted || 0,
            dashboard?.totalCareers || 0,
          ]}
          actionPlansCompleted={[
            dashboard?.actionPlansCompleted || 0,
            dashboard?.totalActionPlans || 0,
          ]}
          performance={dashboard?.actionPlanPerformance || 0}
          loading={loadingDashboard}
        />
      </section>

      <section className="space-y-3">
        <SectionTitle
          icon={<Star className="h-4 w-4 text-amber-500" />}
          title={t("Appreciations")}
        />
        <KPIOrgAppreciations
          abandoned={[
            dashboard?.appreciationAbandoned || 0,
            dashboard?.totalAppreciations || 0,
          ]}
          answered={[
            (dashboard?.appreciationAchievedOnTime ?? 0) +
              (dashboard?.appreciationAchievedOutOfTime ?? 0),
            dashboard?.totalAppreciations || 0,
          ]}
          performance={dashboard?.actionPlanPerformance || 0}
          loading={loadingDashboard}
        />
      </section>
    </div>
  );
}
